import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/useUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import { fetchScheduledPromptsForProject } from "../dataaccess/fetchLLMScheduledPrompts";
import {
  SelectElement,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";
import { triggersByProjectIdAndTriggerType } from "../graphql/queries";
import { createLLMTrigger, updateLLMTrigger } from "../graphql/mutations";
import { CreateLLMTriggerInput, UpdateLLMTriggerInput, LLMTriggerStatus, LLMScheduledPromptStatus } from "../API";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const schema = z.object({
  conversationEndTemplate: z.string().optional(),
  callToActionTemplate: z.string().optional(),
  feedbackTemplate: z.string().optional(),
  interactionTemplate: z.string().optional(),
  sentimentTemplate: z.string().optional(),
  newCustomerTemplate: z.string().optional(),
  lowTokensTemplate: z.string().optional(),
  serviceErrorsTemplate: z.string().optional(),
  lowBalanceTemplate: z.string().optional(),
  toxicContentTemplate: z.string().optional(),
  blacklistedWordTemplate: z.string().optional(),
  piiBlockedTemplate: z.string().optional(),
  apiKeyErrorTemplate: z.string().optional(),
  mcpServerNotWhitelistedTemplate: z.string().optional(),
});

type TriggersFormType = z.infer<typeof schema>;

const Triggers: React.FC = () => {
  const navigate = useNavigate();
  const { project, entitlements, trialDaysRemaining } = useUser();
  const { logError } = useErrorLogging();
  const [selectedTab, setSelectedTab] = useState(0);

  const queryClient = useQueryClient();

  const mutationCreateTrigger = useMutation({
    mutationFn: async (input: CreateLLMTriggerInput) => {
      return API.graphql(graphqlOperation(createLLMTrigger, { input })) as Promise<any>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["triggers"] });
    },
    onError: (error) => {
      logError("Error creating trigger", error as any);
    },
  });

  const mutationUpdateTrigger = useMutation({
    mutationFn: async (input: UpdateLLMTriggerInput) => {
      return API.graphql(graphqlOperation(updateLLMTrigger, { input })) as Promise<any>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["triggers"] });
    },
    onError: (error) => {
      logError("Error updating trigger", error as any);
    },
  });

  const {
    data: triggersData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["triggers", { projectId: project?.id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(triggersByProjectIdAndTriggerType, { 
          projectId: project?.id 
        })
      ) as Promise<any>,
    enabled: !!project?.id,
  });

  const {
    data: templatesData,
    isLoading: isLoadingTemplates,
    isError: isErrorTemplates,
    error: templatesError,
  } = useQuery({
    queryKey: ["scheduledPrompts", "templates", project?.id],
    queryFn: () => fetchScheduledPromptsForProject(project?.id ?? "", LLMScheduledPromptStatus.TEMPLATE),
    enabled: !!project?.id,
  });

  const { handleSubmit, control, reset, watch, setValue, formState } =
    useForm<TriggersFormType>({
      resolver: zodResolver(schema),
    });

  // Single mapping between trigger types and form field names
  const triggerFieldMap: { [key: string]: string } = {
    "CONVERSATION_END": "conversationEndTemplate",
    "CALL_TO_ACTION_CLICKED": "callToActionTemplate",
    "CONVERSATION_FEEDBACK": "feedbackTemplate",
    "CONVERSATION_INTERACTION": "interactionTemplate",
    "CONVERSATION_SENTIMENT": "sentimentTemplate",
    "NEW_CUSTOMER_FIRST_CALL": "newCustomerTemplate",
    "CUSTOMER_LOW_TOKENS": "lowTokensTemplate",
    "SERVICE_ERRORS": "serviceErrorsTemplate",
    "TOXIC_CONTENT_BLOCKED": "toxicContentTemplate",
    "BLACKLISTED_WORD_BLOCKED": "blacklistedWordTemplate",
    "LOW_API_CALL_BALANCE": "lowBalanceTemplate",
    "PII_BLOCKED": "piiBlockedTemplate",
    "API_KEY_ERROR": "apiKeyErrorTemplate",
    "MCP_SERVER_NOT_WHITELISTED": "mcpServerNotWhitelistedTemplate",
  };

  // Create reverse mapping for field names to trigger types
  const fieldTriggerMap = Object.fromEntries(
    Object.entries(triggerFieldMap).map(([triggerType, fieldName]) => [fieldName, triggerType])
  );

  // Load existing triggers and set form values
  useEffect(() => {
    if (triggersData?.data?.triggersByProjectIdAndTriggerType?.items) {
      const triggers = triggersData.data.triggersByProjectIdAndTriggerType.items;

      triggers.forEach((trigger: any) => {
        const fieldName = triggerFieldMap[trigger.triggerType];
        if (fieldName && trigger.scheduledPromptId) {
          setValue(fieldName as any, trigger.scheduledPromptId);
        }
      });
    }
  }, [triggersData, setValue]);

  if (isLoading || isLoadingTemplates) return <div>Loading...</div>;
  if (isError || isErrorTemplates){
    console.log("Error loading triggers or templates", error, templatesError);
    return <div>Error loading triggers or templates</div>;
  }

  const templateOptions = [
    { id: "", label: "Select a template..." },
    { id: "CREATE_NEW", label: "Create new template..." },
    ...(templatesData?.data?.scheduledPromptsByProjectId?.items?.map((template: any) => ({
      id: template?.id || "",
      label: template?.title || "Untitled Template",
    })) ?? []),
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleTemplateChange = async (fieldName: string, value: string) => {
    if (value === "CREATE_NEW") {
      navigate('/scheduledprompts/new?type=template');
    } else {
      setValue(fieldName as any, value);

      const triggerType = fieldTriggerMap[fieldName];
      if (triggerType) {
        await createOrUpdateTrigger(triggerType, value);
      }
    }
  };

  const createOrUpdateTrigger = async (triggerType: string, templateId: string) => {
    try {
      // Check if there's an active trigger for this trigger type
      const existingTrigger = triggersData?.data?.triggersByProjectIdAndTriggerType?.items?.find(
        (trigger: any) => trigger.triggerType === triggerType && 
                          trigger.projectId === project?.id && 
                          trigger.status === LLMTriggerStatus.ACTIVE
      );

      console.log("Existing active trigger:", existingTrigger);
      console.log("Template ID:", templateId);

      if (existingTrigger) {
        // If template is being cleared (set to empty string), disable the existing trigger
        if (!templateId || templateId === "") {
          const updateInput: UpdateLLMTriggerInput = {
            id: existingTrigger.id,
            scheduledPromptId: "",
            status: LLMTriggerStatus.DISABLED,
          };
          await mutationUpdateTrigger.mutateAsync(updateInput);
          return;
        }

        // If template is being changed to a different one, disable the existing trigger first
        if (existingTrigger.scheduledPromptId !== templateId) {
          const disableInput: UpdateLLMTriggerInput = {
            id: existingTrigger.id,
            status: LLMTriggerStatus.DISABLED,
          };
          await mutationUpdateTrigger.mutateAsync(disableInput);

          // Create new trigger with the new template
          const createInput: CreateLLMTriggerInput = {
            projectId: project?.id ?? "",
            triggerType,
            scheduledPromptId: templateId,
            status: LLMTriggerStatus.ACTIVE,
          };
          await mutationCreateTrigger.mutateAsync(createInput);
        }
        // If same template is selected, no action needed (trigger is already active with this template)
      } else if (templateId && templateId !== "") {
        // No existing active trigger, create new one if templateId is provided
        const createInput: CreateLLMTriggerInput = {
          projectId: project?.id ?? "",
          triggerType,
          scheduledPromptId: templateId,
          status: LLMTriggerStatus.ACTIVE,
        };
        await mutationCreateTrigger.mutateAsync(createInput);
      }
    } catch (error) {
      logError("Error creating/updating trigger", error as any);
    }
  }

  return (
    <Box p={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4">Triggers</Typography>
        <Chip label="Beta" size="small" color="primary" variant="outlined" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure automated triggers that respond to specific events and conditions in your conversational AI system.
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Conversation" />
          <Tab label="Customer" />
          <Tab label="Reliability" />
          <Tab label="Safety" />
          <Tab label="Admin" />
        </Tabs>
      </Box>

      {/* Conversation Tab */}
      {selectedTab === 0 && (
        <Stack spacing={2}>
          {/* Conversation End Trigger */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">
                    Conversation End Trigger
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when a conversation has been inactive for a specified period of time.
                  </Typography>
                </Box>
                <SelectElement
                  name="conversationEndTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("conversationEndTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation that ended
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callCount{"}"}{"}"}  - Total number of calls in the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}mostRecentCallCreatedAt{"}"}{"}"}  - ISO timestamp of when the most recent call was created
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}mostRecentCallId{"}"}{"}"}  - Unique identifier for the most recent call in the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}mostRecentCall{"}"}{"}"}  - JSON string containing the full most recent call object
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}earliestCallCreatedAt{"}"}{"}"}  - ISO timestamp of when the earliest call was created
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}earliestCallId{"}"}{"}"}  - Unique identifier for the earliest call in the conversation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Call to Action Clicked Trigger */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Call to Action Clicked</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when users click on call-to-action buttons in conversations.
                  </Typography>
                </Box>
                <SelectElement
                  name="callToActionTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("callToActionTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}action{"}"}{"}"}  - The interaction action that occurred (always cta)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}prompt{"}"}{"}"}  - The original prompt that was sent
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}response{"}"}{"}"}  - The AI's response to the prompt
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}messages{"}"}{"}"}  - JSON array of all messages in the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}email{"}"}{"}"}  - The email address associated with the interaction
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Conversation Feedback Trigger */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Call Feedback (Thumbs Up/Down)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when users provide feedback through thumbs up/down interactions.
                  </Typography>
                </Box>
                <SelectElement
                  name="feedbackTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("feedbackTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}action{"}"}{"}"}  - The feedback action taken (thumbsUp or thumbsDown)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call 
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}comment{"}"}{"}"}  - User's feedback comment (if provided)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}quality{"}"}{"}"}  - Quality rating given by the user and all other +1 for thumbs up, -1 thumbs down 
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}allComments{"}"}{"}"}  - All quality comments for this call
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}prompt{"}"}{"}"}  - The original prompt that was sent
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}response{"}"}{"}"}  - The AI's response to the prompt
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}messages{"}"}{"}"}  - JSON array of all messages in the conversation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Conversation Interaction Trigger */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Conversation Interaction</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions on specific conversation interactions or milestones.
                  </Typography>
                </Box>
                <SelectElement
                  name="interactionTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("interactionTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}action{"}"}{"}"}  - The interaction action that occurred (copy, email, save, suggestion, action)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}prompt{"}"}{"}"}  - The original prompt that was sent
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}response{"}"}{"}"}  - The AI's response to the prompt
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}messages{"}"}{"}"}  - JSON array of all messages in the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}email{"}"}{"}"}  - The email address associated with the interaction
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Conversation Sentiment */}
          <Accordion disabled>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.disabled' }}>
                    Conversation Sentiment
                    <Chip label="Coming Soon" size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions based on the detected sentiment of the conversation.
                  </Typography>
                </Box>
                <SelectElement
                  name="sentimentTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("sentimentTemplate", value)}
                  disabled
                />
              </Box>
            </AccordionSummary>
          </Accordion>
        </Stack>
      )}

      {/* Customer Tab */}
      {selectedTab === 1 && (
        <Stack spacing={2}>
          {/* New Customer First Call */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">New Customer First Call</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger special actions when a new customer starts their first conversation.
                  </Typography>
                </Box>
                <SelectElement
                  name="newCustomerTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("newCustomerTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}prompt{"}"}{"}"}  - The original prompt that was sent
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customer{"}"}{"}"}  - JSON string containing full customer object details
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Customer Low Tokens */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Customer Low Tokens</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger alerts when a customer's token balance is running low.
                  </Typography>
                </Box>
                <SelectElement
                  name="lowTokensTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("lowTokensTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}tokensRemaining{"}"}{"}"}  - Number of tokens remaining in the customer's balance
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation (if available)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customer{"}"}{"}"}  - JSON string containing full customer object details
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      {/* Reliability Tab */}
      {selectedTab === 2 && (
        <Stack spacing={2}>
          {/* Service Errors */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Service Errors</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger alerts when consecutive service failures occur.
                  </Typography>
                </Box>
                <SelectElement
                  name="serviceErrorsTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("serviceErrorsTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call that failed
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentId{"}"}{"}"}  - Unique identifier for the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentName{"}"}{"}"}  - Display name of the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}vendor{"}"}{"}"}  - The vendor/provider of the service that failed
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}serviceName{"}"}{"}"}  - Display name of the service that failed
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}error{"}"}{"}"}  - The error message returned by the service
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}status{"}"}{"}"}  - The HTTP status code of the failed response
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}prompt{"}"}{"}"}  - The original prompt that was sent when the error occurred
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* API Key Error */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">API Key Error</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger alerts when API key retrieval or validation fails.
                  </Typography>
                </Box>
                <SelectElement
                  name="apiKeyErrorTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("apiKeyErrorTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}serviceId{"}"}{"}"}  - Unique identifier for the service that failed
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}serviceName{"}"}{"}"}  - Display name of the service that failed
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}vendor{"}"}{"}"}  - The vendor/provider of the service (e.g., OpenAI, Anthropic)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      {/* Safety Tab */}
      {selectedTab === 3 && (
        <Stack spacing={2}>
          {/* Toxic Content Blocked */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Toxic Content Blocked</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when toxic content is detected and blocked in conversations.
                  </Typography>
                </Box>
                <SelectElement
                  name="toxicContentTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("toxicContentTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call where toxic content was detected
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentId{"}"}{"}"}  - Unique identifier for the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentName{"}"}{"}"}  - Display name of the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}offendingPrompt{"}"}{"}"}  - The original prompt that contained toxic content
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}toxicity{"}"}{"}"}  - JSON object containing toxicity analysis results and scores
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Blacklisted Word/Phrase Blocked */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">Blacklisted Word/Phrase Blocked</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when blacklisted words or phrases are detected and blocked.
                  </Typography>
                </Box>
                <SelectElement
                  name="blacklistedWordTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("blacklistedWordTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call where blacklisted content was detected
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentId{"}"}{"}"}  - Unique identifier for the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentName{"}"}{"}"}  - Display name of the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}offendingPrompt{"}"}{"}"}  - The original prompt that contained blacklisted words/phrases
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}matches{"}"}{"}"}  - JSON array of blacklisted words/phrases that were detected
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* PII Blocked */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">PII Blocked</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when personally identifiable information is detected and blocked.
                  </Typography>
                </Box>
                <SelectElement
                  name="piiBlockedTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("piiBlockedTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call where PII was detected
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentId{"}"}{"}"}  - Unique identifier for the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentName{"}"}{"}"}  - Display name of the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}offendingPrompt{"}"}{"}"}  - The original prompt that contained PII
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}entities{"}"}{"}"}  - JSON array of detected PII entities with types and locations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* MCP Server Not Whitelisted */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6">MCP Server Not Whitelisted</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger actions when an MCP server is used that is not on the whitelist.
                  </Typography>
                </Box>
                <SelectElement
                  name="mcpServerNotWhitelistedTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("mcpServerNotWhitelistedTemplate", value)}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Available parameters for template prompt and email subject:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}callId{"}"}{"}"}  - Unique identifier for the call where non-whitelisted MCP server was used
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectId{"}"}{"}"}  - Unique identifier for the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}projectName{"}"}{"}"}  - Display name of the project
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerId{"}"}{"}"}  - Unique identifier for the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}customerName{"}"}{"}"}  - Display name of the customer
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentId{"}"}{"}"}  - Unique identifier for the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}agentName{"}"}{"}"}  - Display name of the agent (if applicable)
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}conversationId{"}"}{"}"}  - Unique identifier for the conversation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      {"{"}{"{"}servers{"}"}{"}"}  - Information about the non-whitelisted servers that were attempted to be used
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {/* Add quick actions here soon */}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      {/* Admin Tab */}
      {selectedTab === 4 && (
        <Stack spacing={2}>
          {/* Low API Call Balance */}
          <Accordion disabled>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.disabled' }}>
                    Low API Call Balance
                    <Chip label="Coming Soon" size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trigger alerts when API call balance is below a specified threshold.
                  </Typography>
                </Box>
                <SelectElement
                  name="lowBalanceTemplate"
                  label="Select Template"
                  control={control}
                  size="small"
                  options={templateOptions}
                  sx={{ minWidth: 200 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(value) => handleTemplateChange("lowBalanceTemplate", value)}
                  disabled
                />
              </Box>
            </AccordionSummary>
          </Accordion>
        </Stack>
      )}
    </Box>
  );
};

export default Triggers;
